import React, { useState } from 'react';

/**
 * BulkActionBar — contextual dark action bar for row selection.
 * Slides in directly below the table toolbar (not a floating pill, not a modal).
 *
 * Built-in "set value" action: pass `actionLabel`, `options`, `applyLabel`,
 * and `onApply(value)`. Additional buttons can be passed as `children`.
 */
function BulkActionBar({
  count,
  onClear,
  actionLabel,
  options,
  placeholder = 'Choose…',
  applyLabel = 'Apply',
  onApply,
  applying = false,
  allowEmptyApply = false,
  noun = 'selected',
  children,
}) {
  const [cv, setCv] = useState('');
  const canApply = (allowEmptyApply || cv !== '') && !applying;

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', minHeight: 44,
        background: 'var(--lodge-onyx-900)', animation: 'it-bulkIn .12s ease-out', flexShrink: 0, flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--lodge-onyx-50)', flexShrink: 0 }}>
        {count} {noun}
      </span>

      {(options || children) && <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,.12)', flexShrink: 0 }} />}

      {options && (
        <>
          {actionLabel && (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', flexShrink: 0, whiteSpace: 'nowrap' }}>{actionLabel}</span>
          )}
          <select
            value={cv}
            onChange={(e) => setCv(e.target.value)}
            disabled={applying}
            style={{
              background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)',
              borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', cursor: 'pointer',
              minWidth: 168, fontFamily: 'var(--font-sans)',
              color: cv ? '#fff' : 'rgba(255,255,255,.3)',
            }}
          >
            <option value="">{placeholder}</option>
            {options.map((o) => (
              <option key={o.value} value={o.value} style={{ color: '#000' }}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { if (canApply) { onApply(cv === '' ? null : cv); setCv(''); } }}
            disabled={!canApply}
            style={{
              padding: '5px 14px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
              cursor: canApply ? 'pointer' : 'not-allowed', transition: 'background .1s', fontFamily: 'var(--font-sans)', flexShrink: 0,
              background: canApply ? 'var(--lodge-orange-400)' : 'rgba(255,255,255,.07)',
              color: canApply ? '#fff' : 'rgba(255,255,255,.2)',
            }}
          >
            {applying ? 'Applying…' : applyLabel}
          </button>
        </>
      )}

      {children}

      <button
        type="button"
        onClick={onClear}
        style={{
          marginLeft: 'auto', padding: '5px 12px', background: 'transparent', flexShrink: 0,
          border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, fontSize: 12,
          color: 'rgba(255,255,255,.45)', cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'background .1s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.07)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        Clear
      </button>
    </div>
  );
}

export default BulkActionBar;
